import _ from 'lodash';
import { Module } from 'vuex';
import * as types from '../mutation-types';
import api from '../../api/modules/events';
import authApi from '../../api/modules/auth';
import { pick } from 'lodash';
import { formattedEvent, getAddToGoogleCalendarLink } from '@/core/calendar';
import moment from 'moment';
import { saveAs } from '@/web/utils/save-file';

interface State {
  event: EventResponse;
  events: EventResponse[] | any[];
  eventsLoading: boolean;
  selectedEventType: string | null;
  currentViewType: string | null;
  teacherCalendarSelectedDate: Date | null;
  eventCategories: EventCategoryResponse[] | any[];
}
const initialState: State = {
  event: {} as EventResponse,
  events: [],
  eventsLoading: false,
  selectedEventType: null,
  currentViewType: 'dayGridMonth',
  teacherCalendarSelectedDate: null,
  eventCategories: [],
};
const eventModule: Module<State, any> = {
  namespaced: true,
  state: initialState,
  getters: {
    event: state => state.event,
    eventCategories: state => state.eventCategories,
    teacherCalendarSelectedDate: state => state.teacherCalendarSelectedDate,
    hasEventEnded: state => {
      const { event_end_datetime } = state.event;
      if (!event_end_datetime) return false;
      return moment().isAfter(event_end_datetime);
    },
    eventId: state => state.event?.id,
    eventDetails: (state, getters, rootState, rootGetters) => {
      if (!state.event.id) return null;
      const start = state.event.event_start_datetime;
      const end = state.event.event_end_datetime;
      const isLoggedIn = !!rootGetters['user/accessToken'];
      return formattedEvent(state.event, start, end, isLoggedIn);
    },
    eventInstructors: state => state.event?.instructors,
    eventSubstituteInstructors: state => state.event?.substitute_instructors,
    events: state => state.events,
    registerWithAttended: state => {
      const { event_end_datetime, registrations } = state.event;
      if (!event_end_datetime) return [];
      const hasEnded = moment().isAfter(event_end_datetime);
      const registrationStatus = hasEnded ? 'no-show' : 'registered';
      const all = [];
      registrations?.forEach(item => {
        let status = registrationStatus;
        if (item.reason === 'recording') {
          status = item.reason;
        }
        if (item.attended) {
          status = 'attended';
        }
        // @ts-ignore
        all.push({ status, item });
      });
      return all;
    },
    registerWithAttendedNumber: state => {
      if (!state.event?.registrations) return 0;
      return state.event.registrations.length;
    },
    registerWithAttendedDesc: state => {
      if (!state.event?.registrations) return '';
      if (state.event.registration_limit > 0) {
        return `${state.event.registrations.length}/${state.event.registration_limit}`;
      } else {
        return `${state.event.registrations.length}`;
      }
    },
    zoomJoinUrl: state => (state.event ? state.event.zoom_join_url : null),
    streamingSessionId: state =>
      state.event ? state.event.streaming_session_id : null,
    streamingToken: state => (state.event ? state.event.streaming_token : null),
    streamChatToken: state =>
      state.event ? state.event.stream_chat_token : null,
    chatChannelId: state => (state.event ? state.event.chat_channel_id : null),
    eventName: state => (state.event ? state.event.event_name : null),
    getFilteredEvents: state =>
      state.events?.filter(
        event =>
          !state.selectedEventType ||
          event.event_type == state.selectedEventType
      ),
    selectedEventType: state => state.selectedEventType,
    currentViewType: state => state.currentViewType,
    eventsLoading: state => state.eventsLoading,
    eventProducts: state => {
      return _.uniqBy(
        [...(state.event.all_products || []), ...(state.event.products || [])],
        product => product.id
      );
    },
    addToGoogleCalendarLink: state => {
      if (!state.event.id) return null;
      const start = state.event.event_start_datetime;
      const end = state.event.event_end_datetime;
      return getAddToGoogleCalendarLink(start, end, state.event);
    },
  },
  actions: {
    setCurrentEvent({ commit }, event: EventResponse): void {
      commit(types.SET_EVENT, event);
    },
    setTeacherCalendarSelectedDate({ commit }, date: Date) {
      commit(types.SET_TEACHER_CALENDAR_SELECTED_DATE, date);
    },
    // STUDENT
    async getStudioEventDetails(
      { commit },
      { studioURL, eventId, accessCode }
    ): Promise<void> {
      // this retrieves event data for student
      const event = await api.getStudioEventDetails(
        studioURL,
        eventId,
        accessCode
      );
      commit(types.SET_EVENT, event);
    },
    async registerForEvent(
      { commit },
      { studioURL, eventId }
    ): Promise<EventResponse> {
      const result = await api.registerForEvent(studioURL, eventId);
      commit(types.SET_EVENT, result);
      return result;
    },
    async registerForEventWaitlist(
      { commit },
      { studioURL, eventId }
    ): Promise<EventResponse> {
      const result = await api.registerForEventWaitlist(studioURL, eventId);
      commit(types.SET_EVENT, result);
      return result;
    },
    async registerForRecording(
      { commit },
      { studioURL, eventId }
    ): Promise<EventResponse> {
      const result = await api.registerForRecording(studioURL, eventId);
      commit(types.SET_EVENT, result);
      return result;
    },
    async registerForFreeEvent(
      { commit },
      { studioURL, eventId, email, first_name, last_name }
    ): Promise<EventResponse> {
      const result = await api.registerForFreeEvent(
        studioURL,
        eventId,
        email,
        first_name,
        last_name
      );
      commit(types.SET_EVENT, result);
      return result;
    },
    async unregisterFromEvent(
      { commit },
      { studioURL, eventId }
    ): Promise<EventResponse> {
      const result = await api.unregisterFromEvent(studioURL, eventId);
      commit(types.SET_EVENT, result);
      return result;
    },
    async unregisterFromEventWaitlist(
      { commit },
      { studioURL, eventId }
    ): Promise<EventResponse> {
      const result = await api.unregisterFromEventWaitlist(studioURL, eventId);
      commit(types.SET_EVENT, result);
      return result;
    },
    async markEventAttended(
      _,
      { studioURL, eventId, accessCode }
    ): Promise<void> {
      const result = await api.markEventAttended(
        studioURL,
        eventId,
        accessCode
      );
      return result;
    },
    async teacherMarkEventAttended(
      { commit },
      { eventId, eventRegistrationId }
    ): Promise<void> {
      const result = await api.teacherMarkEventAttended(
        eventId,
        eventRegistrationId
      );
      commit(types.SET_REGISTRATION_ATTENDED, eventRegistrationId);
      return result;
    },
    async teacherRemoveEventAttendance(
      { commit },
      { eventId, eventRegistrationId }
    ): Promise<void> {
      const result = await api.teacherRemoveEventAttendance(
        eventId,
        eventRegistrationId
      );
      commit(types.REMOVE_REGISTRATION_ATTENDED, eventRegistrationId);
      return result;
    },
    async customerMarkEventAttended(
      _,
      { eventId, eventRegistrationId }
    ): Promise<void> {
      const result = await api.teacherMarkEventAttended(
        eventId,
        eventRegistrationId
      );
      return result;
    },
    async coustomerRemoveEventAttendance(
      _,
      { eventId, eventRegistrationId }
    ): Promise<void> {
      const result = await api.teacherRemoveEventAttendance(
        eventId,
        eventRegistrationId
      );
      return result;
    },
    async refundCustomerPunch(_, { customerId, punchId }): Promise<any> {
      const result = await authApi.studentIdRefundPunch(customerId, punchId);
      return result;
    },
    // TEACHER
    async getEventDetails({ commit }, eventId): Promise<EventResponse> {
      commit(types.SET_EVENT, null);
      // this retrieves event data for student
      const event = await api.getEventDetails(eventId);
      commit(types.SET_EVENT, event);
      return event;
    },
    async getEvents({ commit }, params): Promise<EventResponse[]> {
      commit(types.SET_EVENTS_LOADING, true);
      commit(types.SET_EVENTS, []);
      const { fromDate, toDate } = pick(params, ['fromDate', 'toDate']);
      const result = await api.getCalendarEvents(fromDate, toDate);
      commit(types.SET_EVENTS, result);
      commit(types.SET_EVENTS_LOADING, false);
      return result;
    },
    async createEvent(_, data: EventResponse): Promise<EventResponse> {
      const result = await api.createEvent(data);
      return result;
    },
    async updateEvent(_, data: EventResponse): Promise<EventResponse> {
      const result = await api.updateEvent(data);
      return result;
    },
    async deleteEvent({ commit, state }): Promise<void> {
      const result = await api.deleteEvent(state.event.id);
      commit(types.SET_EVENT, null);
      return result;
    },
    async cancelEvent({ commit, state }): Promise<EventResponse> {
      commit(types.SET_EVENT_CANCELLED, true);
      const event = await api.updateEvent(state.event);
      commit(types.SET_EVENT, event);
      return event;
    },
    async startStreamingSession({ commit }, eventId): Promise<EventResponse> {
      const event = await api.startStreamingSession(eventId);
      commit(types.SET_EVENT, event);
      return event;
    },
    async stopStreamingSession({ commit }, eventId): Promise<EventResponse> {
      const event = await api.stopStreamingSession(eventId);
      commit(types.SET_EVENT, event);
      return event;
    },
    async startSessionRecording(_, eventId): Promise<EventResponse> {
      const response = await api.startSessionRecording(eventId);
      return response;
    },
    async stopSessionRecording(_, eventId): Promise<EventResponse> {
      const response = await api.stopSessionRecording(eventId);
      return response;
    },
    async createZoomMeeting({ commit }, eventId): Promise<EventResponse> {
      const event = await api.createZoomMeeting(eventId);
      commit(types.SET_EVENT, event);
      return event;
    },
    async scheduleZoomMeeting({ commit }, eventId): Promise<EventResponse> {
      const event = await api.scheduleZoomMeeting(eventId);
      commit(types.SET_EVENT, event);
      return event;
    },
    async refetchZoomMeeting({ commit }, eventId): Promise<EventResponse> {
      const event = await api.getZoomMeeting(eventId);
      commit(types.SET_EVENT, event);
      return event;
    },
    async endZoomMeeting({ commit }, eventId): Promise<EventResponse> {
      const event = await api.endZoomMeeting(eventId);
      commit(types.SET_EVENT, event);
      return event;
    },
    async markZoomMeetingStarted({ commit }, eventId): Promise<EventResponse> {
      const event = await api.markZoomMeetingStarted(eventId);
      commit(types.SET_EVENT, event);
      return event;
    },
    async createGoogleMeetMeeting({ commit }, eventId): Promise<EventResponse> {
      const event = await api.createGoogleMeetMeeting(eventId);
      commit(types.SET_EVENT, event);
      return event;
    },
    async scheduleGoogleMeetMeeting(
      { commit },
      eventId
    ): Promise<EventResponse> {
      const event = await api.scheduleGoogleMeetMeeting(eventId);
      commit(types.SET_EVENT, event);
      return event;
    },
    async refetchGoogleMeetMeeting(
      { commit },
      eventId
    ): Promise<EventResponse> {
      const event = await api.getGoogleMeetMeeting(eventId);
      commit(types.SET_EVENT, event);
      return event;
    },
    async endGoogleMeetMeeting({ commit }, eventId): Promise<EventResponse> {
      const event = await api.endGoogleMeetMeeting(eventId);
      commit(types.SET_EVENT, event);
      return event;
    },
    async deleteAllRegistrations({ commit }, eventId: number): Promise<void> {
      const result = await api.deleteAllRegistrations(eventId);
      commit(types.SET_EVENT_REGISTRATIONS, []);
      return result;
    },
    async emailAllRegistrants(
      { commit },
      { eventId, subject, body }
    ): Promise<void> {
      const result = await api.emailAllRegistrants(eventId, subject, body);
      return result;
    },
    async emailOneRegistrant(
      { commit },
      { eventId, registrationId, subject, body }
    ): Promise<void> {
      const result = await api.emailOneRegistrant(
        eventId,
        registrationId,
        subject,
        body
      );
      return result;
    },
    async emailWaitlistRegistrants(
      { commit },
      { eventId, subject, body }
    ): Promise<void> {
      const result = await api.emailWaitlistRegistrants(eventId, subject, body);
      return result;
    },
    async emailAllAttendees(
      { commit },
      { eventId, subject, body }
    ): Promise<void> {
      const result = await api.emailAllAttendees(eventId, subject, body);
      return result;
    },
    async emailOneUser({ commit }, { userId, subject, body }): Promise<void> {
      const result = await api.emailOneUser(userId, subject, body);
      return result;
    },
    async addEventRegistration(
      { commit },
      { eventId, studentId, allowFree, allowOverflow }
    ): Promise<void> {
      const result = await api.addEventRegistration(
        eventId,
        studentId,
        allowFree,
        allowOverflow
      );
      return result;
    },
    async addEventWaitlistRegistration(
      { commit },
      { eventId, studentId, allowFree }
    ): Promise<void> {
      const result = await api.addEventWaitlistRegistration(
        eventId,
        studentId,
        allowFree
      );
      return result;
    },
    async removeEventRegistration(
      { commit },
      { eventId, registrationId }
    ): Promise<void> {
      const result = await api.removeEventRegistration(eventId, registrationId);
      return result;
    },
    async removeEventWaitlistRegistration(
      { commit },
      { eventId, registrationId }
    ): Promise<void> {
      const result = await api.removeEventWaitlistRegistration(
        eventId,
        registrationId
      );
      return result;
    },
    setCurrentViewType({ commit }, currentViewType) {
      commit(types.SET_CURRENT_VIEW_TYPE, currentViewType);
    },
    async exportEventRegistrationEmails(_, { eventId }) {
      const data = await api.exportEventRegistrationEmails(eventId);
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'event-emails.csv');
    },
    async exportEventWaitlistRegistrationEmails(_, { eventId }) {
      const data = await api.exportEventWaitlistRegistrationEmails(eventId);
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'waitlist-emails.csv');
    },
    async getEventCategories({ commit }): Promise<EventCategoryResponse[]> {
      const result = await api.getEventCategories();
      commit(types.SET_EVENT_CATEGORIES, result);
      return result;
    },
    async getEventRecordings(_, eventId): Promise<EventRecordingResponse[]> {
      return await api.getEventRecordings(eventId);
    },
    async addEventRecording(_, { eventId, fileId }) {
      return await api.addEventRecording(eventId, fileId);
    },
    async deleteEventRecording(_, { eventId, recordingId }) {
      return await api.deleteEventRecording(eventId, recordingId);
    },
    async registerFromWaitlistToEvent(
      _,
      { eventId, waitlistRegistrationId, allowFree, allowOverflow }
    ) {
      return await api.registerFromWaitlistToEvent(
        eventId,
        waitlistRegistrationId,
        allowFree,
        allowOverflow
      );
    },
  },
  mutations: {
    [types.SET_EVENT](state: State, event: EventResponse) {
      state.event = event;
    },
    [types.SET_EVENT_CANCELLED](state: State, cancelled: boolean) {
      state.event.is_cancelled = cancelled;
    },
    [types.SET_EVENT_REGISTRATIONS](
      state: State,
      registrations: RegistrationResponse[]
    ) {
      state.event.registrations = registrations;
    },
    [types.SET_EVENT_CATEGORIES](
      state: State,
      eventCategories: EventCategoryResponse[]
    ) {
      state.eventCategories = eventCategories;
    },
    [types.SET_EVENTS](state: State, events: EventResponse[]) {
      state.events = events;
    },
    [types.SET_SELECTED_EVENT_TYPE](state: State, selectedEventType: string) {
      state.selectedEventType = selectedEventType;
    },
    [types.SET_CURRENT_VIEW_TYPE](state: State, currentViewType: string) {
      state.currentViewType = currentViewType;
    },
    [types.SET_EVENTS_LOADING](state: State, loading: boolean) {
      state.eventsLoading = loading;
    },
    [types.SET_TEACHER_CALENDAR_SELECTED_DATE](state: State, date: Date) {
      state.teacherCalendarSelectedDate = date;
    },
    [types.SET_REGISTRATION_ATTENDED](
      state: State,
      eventRegistrationId: number
    ) {
      const registration = state.event.registrations.find(
        r => r.id === eventRegistrationId
      );
      if (registration) {
        registration.attended = true;
      }
    },
    [types.REMOVE_REGISTRATION_ATTENDED](
      state: State,
      eventRegistrationId: number
    ) {
      const registration = state.event.registrations.find(
        r => r.id === eventRegistrationId
      );
      if (registration) {
        registration.attended = false;
      }
    },
  },
};
export default eventModule;
